import React, { useId, useMemo } from "react";

import {
  RULER_HEIGHT,
  TEXT_WIDTH,
  RULER_TICK_SPACING_TARGET_PX,
  MIN_MAJOR_TICK_SPACING_PX,
  MIN_MINOR_TICK_SPACING_PX,
  TIMELINE_RULER_TEXT_COLOR,
  MINOR_TICK_HEIGHT,
  MAJOR_TICK_HEIGHT,
  TICK_GRADIENT_START,
  TICK_GRADIENT_END,
} from "../../constants";
import { formatTimelineTime, labelDecimals, rulerTicks, showsHours } from "../../utils/ruler";

/** Labels sit a fixed gap above the major ticks, leaving the top of the ruler for the overview strip. */
const LABEL_BASELINE_Y = RULER_HEIGHT - MAJOR_TICK_HEIGHT - 7;

type Props = {
  width: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  durationSeconds: number;
};

export const TimelineRuler: React.FC<Props> = React.memo(({ width, pixelsPerSecond, scrollLeft, durationSeconds }) => {
  const gradientId = useId();
  const ticks = useMemo(() => {
    const { majorStep, major, minor } = rulerTicks({
      startSeconds: scrollLeft / pixelsPerSecond,
      endSeconds: (scrollLeft + width) / pixelsPerSecond,
      durationSeconds,
      pixelsPerSecond,
      targetSpacingPx: RULER_TICK_SPACING_TARGET_PX,
      minMajorSpacingPx: MIN_MAJOR_TICK_SPACING_PX,
      minMinorSpacingPx: MIN_MINOR_TICK_SPACING_PX,
    });
    const decimals = labelDecimals(majorStep);
    const showHours = showsHours(durationSeconds);

    const majorTicks = major.map(({ index, time }) => ({
      key: index,
      x: time * pixelsPerSecond,
      label: formatTimelineTime(time, decimals, showHours),
    }));
    const minorTicks = minor.map((time) => ({ x: time * pixelsPerSecond }));

    return { majorTicks, minorTicks };
  }, [scrollLeft, width, pixelsPerSecond, durationSeconds]);

  return (
    <svg
      aria-hidden
      height={RULER_HEIGHT}
      style={{ position: "absolute", inset: 0, pointerEvents: "none", userSelect: "none", overflow: "hidden" }}
      width={width}
    >
      <defs>
        <linearGradient id={`${gradientId}-minor`} x1="0" x2="0" y1="1" y2="0">
          <stop offset="0%" stopColor={TICK_GRADIENT_START} />
          <stop offset="100%" stopColor={TICK_GRADIENT_END} />
        </linearGradient>
        <linearGradient id={`${gradientId}-major`} x1="0" x2="0" y1="1" y2="0">
          <stop offset="0%" stopColor={TICK_GRADIENT_START} />
          <stop offset="75%" stopColor={TICK_GRADIENT_END} />
          <stop offset="100%" stopColor={TICK_GRADIENT_END} />
        </linearGradient>
      </defs>
      {/* Allow subpixel coverage at reduced zoom; crispEdges can erase a tick entirely. */}
      <g>
        {ticks.minorTicks.map((tick, i) => (
          <rect
            fill={`url(#${gradientId}-minor)`}
            height={MINOR_TICK_HEIGHT}
            key={`minor-${i}`}
            width={1}
            x={Math.round(tick.x - scrollLeft)}
            y={RULER_HEIGHT - MINOR_TICK_HEIGHT}
          />
        ))}
        {ticks.majorTicks.map((tick) => (
          <rect
            fill={`url(#${gradientId}-major)`}
            height={MAJOR_TICK_HEIGHT}
            key={tick.key}
            width={1}
            x={Math.min(width - 1, Math.round(tick.x - scrollLeft))}
            y={RULER_HEIGHT - MAJOR_TICK_HEIGHT}
          />
        ))}
      </g>
      {/* DOM text inherits the app's loaded font, including next/font aliases. */}
      <g
        fill={TIMELINE_RULER_TEXT_COLOR}
        fontSize={10}
        style={{ fontFamily: "inherit", fontVariantNumeric: "tabular-nums" }}
      >
        {ticks.majorTicks.map((tick) => {
          const x = Math.min(width - 1, Math.round(tick.x - scrollLeft));
          // Only edge labels change alignment. Interior labels stay on their ticks.
          const anchor = x < TEXT_WIDTH / 2 ? "start" : x + TEXT_WIDTH / 2 > width ? "end" : "middle";
          return (
            <text key={tick.key} textAnchor={anchor} x={x} y={LABEL_BASELINE_Y}>
              {tick.label}
            </text>
          );
        })}
      </g>
    </svg>
  );
});
TimelineRuler.displayName = "TimelineRuler";
